import { UserPermission } from '@/constants/UserPermission';
import type { Ref } from 'vue';
import { computed, unref } from 'vue';
import useUser from '@/composables/useUser';
import { useAdminBackdoor } from '@/composables/useAdminBackdoor';
import type { RouteRecordRaw } from 'vue-router';
import { getAreAllRouteLevelsAuthorized, type RoutePermissionSource } from '@/utils/permission';
import type { MaybeRef } from '@vueuse/core';

/**
 * Check if the user has the given permission.
 *
 * If the user has not been loaded yet, the computed value will return false.
 *
 * @param permission Permission to check for. If `null` is passed in, this will
 * always return false.
 */
export function useHasPermission(permission: MaybeRef<UserPermission | null>): Ref<boolean> {
  const { user, isUserSuperAdmin } = useUser();

  /**
   * The back end returns the various "impersonate" permissions as a way to indicate that someone is currently impersonating,
   * so we shouldn't always return `true` for admins for these permissions.
   *
   * For the read only permissions, these should be refactored such that all users has `write` permissions,
   * but for the time being, we shouldn't always return `true` for admins.
   *
   * Excluding "Risk History" temporarily until the feature is fully tested by the product.
   */
  const excludedFromAdminSupersetPermissions = new Set([
    UserPermission.IMPERSONATE,
    UserPermission.IMPERSONATE_TEAM,
    UserPermission.WARN_IMPERSONATE,
    UserPermission.READ_ONLY,
    UserPermission.READ_ONLY_PLATFORM,
    UserPermission.RISK_HISTORY,
  ]);

  return computed(() => {
    const check = unref(permission);
    if (check == null) {
      return false;
    }

    /**
     * Only super admins have the ring of power (all permissions except the exclusions above).
     * Weak admins (e.g. isAdmin derived from team_roles) must rely on their granular permissions.
     */
    if (!excludedFromAdminSupersetPermissions.has(check) && isUserSuperAdmin.value) return true;

    const perm = user.value?.permission;

    if (perm == null) {
      return false;
    }

    return perm.includes(check);
  });
}

interface ViewableRouteInfo {
  route: RouteRecordRaw;

  /**
   * True if the current user is authorized to view the route.
   *
   * This will be false when the user info have not been loaded yet.
   */
  authorized: boolean;
}

/**
 * Enrich route parents' named children for menu presentation.
 *
 * Pass parent route records (with `children`). Each named child is authorized via
 * `[parent, child]` with the same per-level rules as the navigation guard.
 *
 * This does not remove unauthorized routes from the list. You may need to `v-if` them.
 */
export function useViewableRoutes(routes: RouteRecordRaw[]): Ref<ViewableRouteInfo[]> {
  const { user } = useUser();
  const shouldUseAdminBackDoor = useAdminBackdoor();

  const userPermission = computed(() => new Set(user.value?.permission ?? []));

  return computed(() => {
    return routes.flatMap((parent) =>
      (parent.children ?? [])
        .filter((child) => child.name !== undefined)
        .map(
          (child): ViewableRouteInfo => ({
            route: child,
            authorized: getAreAllRouteLevelsAuthorized({
              permission: userPermission.value,
              routeLevels: [parent, child] as RoutePermissionSource[],
              shouldUseAdminBackDoor: shouldUseAdminBackDoor.value,
            }),
          }),
        ),
    );
  });
}
